import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, TrendingUp, Camera, Star, Shield, Home, ArrowRight, Download, ExternalLink, Search } from 'lucide-react';

const HostResourcesPage = () => {
  const [selectedTag, setSelectedTag] = useState('All');
  const [search, setSearch] = useState('');

  const tags = ['All', 'Getting Started', 'Pricing', 'Guest Experience', 'Operations', 'Legal', 'Marketing', 'Finance'];

  const guides = [
    {
      id: 1,
      icon: '🚀',
      title: 'The Complete Host Starter Guide',
      desc: 'Everything you need to know to launch your first listing on Ariv — from photography tips to setting your first price.',
      tags: ['Getting Started'],
      readTime: '12 min read',
      type: 'guide',
    },
    {
      id: 2,
      icon: '📸',
      title: 'Property Photography That Books',
      desc: 'Professional photos can increase your bookings by up to 40%. Learn how to stage, light, and shoot your property — with or without a professional photographer.',
      tags: ['Getting Started', 'Marketing'],
      readTime: '8 min read',
      type: 'guide',
    },
    {
      id: 3,
      icon: '💷',
      title: 'Dynamic Pricing Masterclass',
      desc: 'How to set base rates, minimum stays, weekend premiums, and seasonal pricing to maximise your revenue without sacrificing occupancy.',
      tags: ['Pricing'],
      readTime: '15 min read',
      type: 'guide',
    },
    {
      id: 4,
      icon: '⭐',
      title: 'Getting & Maintaining 5-Star Reviews',
      desc: 'The guest experience framework used by our top-rated hosts — from arrival instructions to check-out messages.',
      tags: ['Guest Experience'],
      readTime: '10 min read',
      type: 'guide',
    },
    {
      id: 5,
      icon: '🔑',
      title: 'Self-Check-In Setup Guide',
      desc: 'Set up smart locks, key boxes, and automated arrival instructions so your guests can always let themselves in smoothly.',
      tags: ['Operations', 'Guest Experience'],
      readTime: '7 min read',
      type: 'guide',
    },
    {
      id: 6,
      icon: '📋',
      title: 'UK STR Legal Requirements (2025)',
      desc: 'Planning permission, council licensing, insurance requirements, and tax obligations for UK short-term rental operators — updated for 2025.',
      tags: ['Legal', 'Getting Started'],
      readTime: '20 min read',
      type: 'guide',
    },
    {
      id: 7,
      icon: '🏠',
      title: 'Rent-to-Rent (R2R) Fundamentals',
      desc: 'A deep dive into the R2R model — how to find and negotiate with landlords, structure your agreements, and scale your portfolio.',
      tags: ['Finance', 'Getting Started'],
      readTime: '18 min read',
      type: 'guide',
    },
    {
      id: 8,
      icon: '📊',
      title: 'Understanding Your Analytics Dashboard',
      desc: 'Make sense of your occupancy rate, RevPAR, ADR, and conversion metrics — and what to do when they dip.',
      tags: ['Finance', 'Operations'],
      readTime: '9 min read',
      type: 'guide',
    },
    {
      id: 9,
      icon: '🧹',
      title: 'Building a Reliable Cleaning Operation',
      desc: 'How to find, train, and manage cleaning teams — including same-day turnover procedures and quality control checklists.',
      tags: ['Operations'],
      readTime: '11 min read',
      type: 'guide',
    },
    {
      id: 10,
      icon: '🌐',
      title: 'Multi-Channel Distribution Strategy',
      desc: 'How to list on Airbnb, Booking.com, and other platforms alongside Ariv — and sync your calendar to avoid double bookings.',
      tags: ['Marketing', 'Operations'],
      readTime: '13 min read',
      type: 'guide',
    },
    {
      id: 11,
      icon: '💼',
      title: 'STR Tax Guide for UK Hosts',
      desc: 'Income tax, furnished holiday let rules, VAT implications, and allowable expenses — practical guidance for UK short-term rental operators.',
      tags: ['Legal', 'Finance'],
      readTime: '16 min read',
      type: 'guide',
    },
    {
      id: 12,
      icon: '🎯',
      title: 'Winning Your First 10 Reviews',
      desc: 'The critical first weeks as a new host — how to price aggressively to build reviews, then raise rates as social proof grows.',
      tags: ['Getting Started', 'Pricing', 'Marketing'],
      readTime: '8 min read',
      type: 'guide',
    },
  ];

  const templates = [
    { title: 'House Rules Template', desc: 'Customisable house rules covering noise, parking, pets, and check-out procedures.', icon: '📄' },
    { title: 'Guest Welcome Message', desc: 'A warm, professional welcome message template with check-in instructions.', icon: '✉️' },
    { title: 'Cleaning Checklist', desc: 'Room-by-room checklist for your cleaning team to ensure consistent standards.', icon: '✅' },
    { title: 'Property Inventory List', desc: 'Comprehensive inventory template to document all items in your property.', icon: '📦' },
    { title: 'R2R Contract Template', desc: 'Draft Rent-to-Rent agreement template (review with a solicitor before use).', icon: '📝' },
    { title: 'Guest Review Request', desc: 'A polite, effective message to encourage guests to leave a review after checkout.', icon: '⭐' },
  ];

  const videos = [
    { title: 'How to price your first listing', duration: '14:32', thumbnail: '🎬' },
    { title: 'Photography tips from a Superhost', duration: '22:15', thumbnail: '📸' },
    { title: 'Setting up smart home tech', duration: '18:07', thumbnail: '🏡' },
    { title: 'Scaling from 1 to 10 properties', duration: '31:45', thumbnail: '📈' },
  ];

  const filteredGuides = guides.filter(g => {
    const tagMatch = selectedTag === 'All' || g.tags.includes(selectedTag);
    const searchMatch = !search || g.title.toLowerCase().includes(search.toLowerCase()) || g.desc.toLowerCase().includes(search.toLowerCase());
    return tagMatch && searchMatch;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div></Link>
          <div className="flex items-center gap-6">
            <Link to="/host/calculator" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Profit Calculator</Link>
            <Link to="/become-host" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Become a Host</Link>
            <Link to="/login" className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-[#1F3A68] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen size={32} />
            <h1 className="text-4xl font-bold">Host Resource Centre</h1>
          </div>
          <p className="text-white/80 text-lg max-w-2xl mb-8">
            Guides, templates, and tools to help you build a professional, profitable hosting operation.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="bg-white/10 rounded-xl px-5 py-2.5 text-center">
              <div className="font-bold text-xl">{guides.length}</div>
              <div className="text-xs text-white/60">Guides</div>
            </div>
            <div className="bg-white/10 rounded-xl px-5 py-2.5 text-center">
              <div className="font-bold text-xl">{templates.length}</div>
              <div className="text-xs text-white/60">Templates</div>
            </div>
            <div className="bg-white/10 rounded-xl px-5 py-2.5 text-center">
              <div className="font-bold text-xl">{videos.length}</div>
              <div className="text-xs text-white/60">Video tutorials</div>
            </div>
            <div className="bg-white/10 rounded-xl px-5 py-2.5 text-center">
              <div className="font-bold text-xl">Free</div>
              <div className="text-xs text-white/60">All resources</div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Quick links */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {[
            { icon: <TrendingUp size={20} className="text-[#1F3A68]" />, label: 'Profit Calculator', to: '/host/calculator', desc: 'Estimate your monthly returns' },
            { icon: <Home size={20} className="text-[#1F3A68]" />, label: 'List a Property', to: '/become-host', desc: 'Start hosting today' },
            { icon: <Shield size={20} className="text-[#1F3A68]" />, label: 'Deal Marketplace', to: '/host/deals/marketplace', desc: 'Find R2R opportunities' },
            { icon: <Star size={20} className="text-[#1F3A68]" />, label: 'Help Center', to: '/help', desc: 'Get answers to FAQs' },
          ].map((item, i) => (
            <Link key={i} to={item.to} className="flex items-center gap-3 p-5 border border-gray-100 rounded-2xl hover:border-[#1F3A68]/30 hover:bg-[#1F3A68]/5 transition-colors group">
              <div className="bg-[#1F3A68]/10 rounded-xl p-2.5">{item.icon}</div>
              <div>
                <div className="font-semibold text-[#1F3A68] text-sm group-hover:underline">{item.label}</div>
                <div className="text-gray-500 text-xs">{item.desc}</div>
              </div>
              <ArrowRight size={14} className="text-gray-300 ml-auto group-hover:text-[#1F3A68] transition-colors" />
            </Link>
          ))}
        </div>

        {/* Guides */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-[#1F3A68] flex items-center gap-2">
              <BookOpen size={22} />
              Host Guides
            </h2>
          </div>

          {/* Search + filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search guides…" value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]" />
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map(t => (
                <button key={t} onClick={() => setSelectedTag(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    selectedTag === t ? 'bg-[#1F3A68] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>{t}</button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredGuides.map(guide => (
              <div key={guide.id} className="border border-gray-100 rounded-2xl p-6 hover:shadow-md hover:border-[#1F3A68]/20 transition-all cursor-pointer group">
                <div className="text-3xl mb-4">{guide.icon}</div>
                <div className="flex gap-1.5 mb-3 flex-wrap">
                  {guide.tags.map(t => (
                    <span key={t} className="text-[10px] bg-[#1F3A68]/10 text-[#1F3A68] px-2 py-0.5 rounded-full font-medium">{t}</span>
                  ))}
                </div>
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-[#1F3A68] transition-colors">{guide.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{guide.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{guide.readTime}</span>
                  <span className="text-[#1F3A68] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Templates */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#1F3A68] flex items-center gap-2 mb-6">
            <Download size={22} />
            Downloadable Templates
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t, i) => (
              <div key={i} className="flex items-start gap-4 border border-gray-100 rounded-2xl p-5 hover:border-[#1F3A68]/30 hover:bg-[#1F3A68]/5 transition-colors cursor-pointer group">
                <div className="text-2xl flex-shrink-0">{t.icon}</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-[#1F3A68]">{t.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
                </div>
                <Download size={16} className="text-gray-300 group-hover:text-[#1F3A68] flex-shrink-0 mt-0.5 transition-colors" />
              </div>
            ))}
          </div>
        </section>

        {/* Videos */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#1F3A68] flex items-center gap-2 mb-6">
            <Camera size={22} />
            Video Tutorials
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {videos.map((v, i) => (
              <div key={i} className="border border-gray-100 rounded-2xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer group">
                <div className="bg-[#1F3A68]/10 h-36 flex items-center justify-center text-5xl">
                  {v.thumbnail}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-[#1F3A68]">{v.title}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{v.duration}</span>
                    <ExternalLink size={14} className="text-gray-300 group-hover:text-[#1F3A68] transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="bg-[#1F3A68] rounded-3xl p-12 text-white text-center">
          <h2 className="text-3xl font-bold mb-3">Ready to start earning?</h2>
          <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
            Apply everything you've learned and list your first property on Ariv today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/become-host" className="inline-flex items-center justify-center gap-2 bg-white text-[#1F3A68] px-8 py-4 rounded-xl font-bold hover:bg-gray-50 transition-colors">
              List Your Property <ArrowRight size={18} />
            </Link>
            <Link to="/host/calculator" className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white px-8 py-4 rounded-xl font-bold hover:bg-white/10 transition-colors">
              Calculate Your Returns
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1F3A68] text-white py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-[23px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[23px] w-auto object-contain brightness-0 invert" /></div>
          <div className="flex gap-6 text-sm text-white/70">
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/careers" className="hover:text-white">Careers</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
          <p className="text-white/50 text-sm">&copy; {new Date().getFullYear()} Ariv. A product of Brick & Capital Ltd.</p>
          <p className="text-white/40 text-xs mt-2"><strong>Brick &amp; Capital Ltd</strong> &middot; Registered in England and Wales, company number <a href="https://find-and-update.company-information.service.gov.uk/company/16655788" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/70">16655788</a> &middot; Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX</p>
        </div>
      </footer>
    </div>
  );
};

export default HostResourcesPage;
